import { useContext } from 'react';

import { type QueryClient, useQueryClient } from '@tanstack/react-query';

import { invalidateWorkspaces, removeWorkspaceFromCache, writeWorkspace } from './cache.ts';
import { WorkspaceIdContext } from './client.ts';
import type {
    MarkSectionSeenMutationVariables,
    VisitEmployeeRecapMutationVariables,
    VisitRecapMutationVariables,
} from './generated/attention.ts';
import type { MuteEmployeeRecapMutationVariables, MuteRecapMutationVariables } from './generated/notifications.ts';
import type { DeleteRecapMutationVariables } from './generated/recaps.ts';
import type {
    EmployeeRecap,
    NotificationSetting,
    Recap,
    RecapMute,
    SignInResult,
    Source,
    SourceFromLinkResult,
    User,
    Workspace,
} from './generated/schemas.ts';
import type { DeleteSourceMutationVariables } from './generated/sources.ts';
import type { DeleteWorkspaceMutationVariables } from './generated/workspaces.ts';

// What a change does to the cache, by the operation that made it, for the generated mutation hooks (orval.config.ts).
// A change is written into a cached list only where the next step reads it right away (starting a sync); otherwise its
// lists are refetched (plan/docs/structure.md "Generated API schemas").

interface MutationContext {
    queryClient: QueryClient;
    workspaceId: string | null;
}

// Gets the operation's answer and variables (generated/<tag>.ts: `…MutationResult`, `…MutationVariables`). A rule that
// returns a promise holds the mutation until it's settled, e.g. until a list it changed is fetched again.
type MutationRule = (context: MutationContext, data: never, variables: never) => unknown;

const refetchRecapLists = ({ queryClient, workspaceId }: MutationContext) =>
    void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'recaps', 'list'] });

// A source's page lists its recaps and a recap's page its sources, so a change to one refetches everything of the
// other.
const refetchRecaps = ({ queryClient, workspaceId }: MutationContext) =>
    void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'recaps'] });

const refetchSources = ({ queryClient, workspaceId }: MutationContext) =>
    void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'sources'] });

// A new or reprocessed recap starts in `processing` until it's drafted; it's set where it's shown on its own, and its
// lists refetched, and the sources', which list the recaps they're in.
const setRecap = (context: MutationContext, recap: Recap) => {
    context.queryClient.setQueryData(['workspaces', context.workspaceId, 'recaps', recap.public_id], recap);
    refetchRecapLists(context);
    refetchSources(context);
};

// A change to what a recap says answers with the whole recap (plan/DECISIONS.md D42): it's set where it's shown, its
// versions and its lists fetched again, and what publishing it would change dropped, so the publish dialog asks anew.
const setEditedRecap = (context: MutationContext, recap: Recap) => {
    const recapKey = ['workspaces', context.workspaceId, 'recaps', recap.public_id];
    // The overview lists its to-dos and events (plan task 19).
    void context.queryClient.invalidateQueries({ queryKey: ['workspaces', context.workspaceId, 'home'] });
    context.queryClient.setQueryData(recapKey, recap);
    void context.queryClient.invalidateQueries({ queryKey: [...recapKey, 'versions'] });
    context.queryClient.removeQueries({ queryKey: [...recapKey, 'draft'] });
    refetchRecapLists(context);
};

// A new or reprocessed source starts in `processing`; a reprocessed one with the recaps it's in, drafted again.
const setSource = (context: MutationContext, source: Source) => {
    context.queryClient.setQueryData(['workspaces', context.workspaceId, 'sources', source.id], source);
    void context.queryClient.invalidateQueries({ queryKey: ['workspaces', context.workspaceId, 'sources', 'list'] });
    refetchRecaps(context);
};

// A new source, uploaded or from a link. Waits for the list, so the upload's row is replaced by the source's without the
// list jumping in between.
const addSource = (context: MutationContext, source: Source) => {
    context.queryClient.setQueryData(['workspaces', context.workspaceId, 'sources', source.id], source);
    return context.queryClient.invalidateQueries({
        queryKey: ['workspaces', context.workspaceId, 'sources', 'list'],
    });
};

// The projects too, which hold the same teams, counts and stars as the clients' (plan/DECISIONS.md D185).
const refetchClientsAndWait = ({ queryClient, workspaceId }: MutationContext) =>
    Promise.all([
        queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'clients'] }),
        queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'projects'] }),
    ]);

const refetchClients = (context: MutationContext) => void refetchClientsAndWait(context);

// Clients carry how many recaps and sources they have, and recaps and sources show their client's and project's names,
// so a change to either side refetches the other.
const changeClientsAndWait = (context: MutationContext) => {
    refetchRecaps(context);
    refetchSources(context);
    return refetchClientsAndWait(context);
};

const changeClients = (context: MutationContext) => void changeClientsAndWait(context);

// A comment's thread is its recap's or source's, which an edit or deletion doesn't name, so every thread is fetched
// again; the Aktivitäten list a new one, as do a recap's, also an employee's.
const refetchComments = ({ queryClient, workspaceId }: MutationContext) => {
    void queryClient.invalidateQueries({
        queryKey: ['workspaces', workspaceId],
        predicate: (query) => query.queryKey.at(-1) === 'comments' || query.queryKey.includes('activities'),
    });
};

// A change to a team, and to who reads a project, is held until the clients are fetched again: their sections save each
// change at once and show it until then (useAutoSave), and the "Meine" filter shows it at once.
const changeTeams = (context: MutationContext) => {
    void context.queryClient.invalidateQueries({ queryKey: ['workspaces', context.workspaceId, 'home'] });
    return refetchClientsAndWait(context);
};

// Whom a recap's published version asks follows who reads its project, and who's taken off is in the Aktivitäten
// (plan/DECISIONS.md D104).
const changeReaders = (context: MutationContext) => {
    refetchRecaps(context);
    void context.queryClient.invalidateQueries({
        queryKey: ['workspaces', context.workspaceId],
        predicate: (query) => query.queryKey.includes('activities'),
    });
    return refetchClientsAndWait(context);
};

// The members' list, and a client's employee's below 'client'.
const refetchConnections = ({ queryClient, workspaceId }: MutationContext) =>
    void queryClient.invalidateQueries({
        queryKey: ['workspaces', workspaceId],
        predicate: (query) => query.queryKey.includes('connections'),
    });

// A source names the feed it came from, which deleting takes away.
const removeConnections = (context: MutationContext) => {
    refetchConnections(context);
    refetchSources(context);
};

// The workspace's API connection makes, names and lets go of the bot it acts as, which the members list (plan task 69).
// A provider's companies are linked to clients, which show them (plan/DECISIONS.md D176), and go with the feed that
// keeps them.
const refetchExternalCompanies = (context: MutationContext) => {
    void context.queryClient.invalidateQueries({
        queryKey: ['workspaces', context.workspaceId, 'external-companies'],
    });
    refetchClients(context);
};

const changeConnectionsAndMembers = (context: MutationContext) => {
    removeConnections(context);
    void context.queryClient.invalidateQueries({ queryKey: ['workspaces', context.workspaceId, 'members'] });
};

const setCurrentUser = ({ queryClient }: MutationContext, user: User) => queryClient.setQueryData(['me'], user);

// The avatar shows wherever the user does: members, comments, recaps.
const setCurrentUserEverywhere = (context: MutationContext, user: User) => {
    setCurrentUser(context, user);
    void context.queryClient.invalidateQueries({ queryKey: ['workspaces'] });
};

// Whatever was cached belonged to nobody or to someone else.
const signedIn = (context: MutationContext, user: User) => {
    context.queryClient.clear();
    setCurrentUser(context, user);
};

const writeWorkspaceTo = ({ queryClient }: MutationContext, workspace: Workspace) =>
    writeWorkspace(queryClient, workspace);

// What's new on a section's page is seen once it's marked so; the Übersicht shows the Aktivitäten's (plan task 61).
const refetchSeenList = (
    { queryClient, workspaceId }: MutationContext,
    _: unknown,
    { section }: MarkSectionSeenMutationVariables
) => {
    void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, section, 'list'] });
    if (section === 'activities') void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'home'] });
};

// Opening a recap or a source sees what happened to it, and a recap its sources.
const refetchNews = ({ queryClient, workspaceId }: MutationContext) => {
    for (const key of ['activities', 'sources']) {
        void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, key, 'list'] });
    }
    void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'home'] });
};

const refetchInvitations = ({ queryClient, workspaceId }: MutationContext) =>
    void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'invitations'] });

const refetchHome = ({ queryClient, workspaceId }: MutationContext) =>
    void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'home'] });

// A client from the start's suggestions files its emails and drafts its recaps (plan task 124); skipping the clients
// drafts one too.
const changeStart = (context: MutationContext) => {
    refetchHome(context);
    refetchClients(context);
    refetchRecaps(context);
    refetchSources(context);
};

const MUTATION_RULES: Record<string, MutationRule> = {
    // Account
    // A sign-in code (plan task 78). One that made the account keeps the cache: nothing's in it but the invitation the
    // signup may come from, which the flow still shows.
    verifySignInCode: (context, result: SignInResult) =>
        result.is_new ? setCurrentUser(context, result.user) : signedIn(context, result.user),
    updateMe: setCurrentUser,
    changeEmail: setCurrentUser,
    verifyEmail: setCurrentUser,
    requestMyAccess: setCurrentUser,
    uploadMyAvatar: setCurrentUserEverywhere,
    deleteMyAvatar: setCurrentUserEverywhere,
    // Its user lists its Google sign-in (plan task 74).
    deleteMySsoIdentity: ({ queryClient }) => void queryClient.invalidateQueries({ queryKey: ['me'] }),

    // Workspaces
    createWorkspace: writeWorkspaceTo,
    updateWorkspace: writeWorkspaceTo,
    renewWorkspaceEmailAddress: writeWorkspaceTo,
    updateWorkspaceEmailAddress: writeWorkspaceTo,
    uploadWorkspaceLogo: writeWorkspaceTo,
    deleteWorkspaceLogo: writeWorkspaceTo,
    acceptInvitation: writeWorkspaceTo,
    deleteWorkspace: ({ queryClient }, _: void, { workspaceId }: DeleteWorkspaceMutationVariables) =>
        removeWorkspaceFromCache(queryClient, workspaceId),
    createInvitation: refetchInvitations,
    deleteInvitation: refetchInvitations,
    // Leaving takes the workspace out of the list.
    leaveWorkspace: ({ queryClient, workspaceId }) => removeWorkspaceFromCache(queryClient, workspaceId!),
    // Deleting a bot deletes its connection (plan task 69).
    removeMember: changeConnectionsAndMembers,
    // The own role in the workspace list changes when it's handed over, and how a disabled member reads everywhere in the
    // workspace (plan task 64).
    updateMember: ({ queryClient, workspaceId }) => {
        void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId] });
        invalidateWorkspaces(queryClient);
    },

    // Clients and projects
    // A client's and a project's pages find their slug in the lists, so saving one that's opened next, under its new
    // slug, is held until they're fetched again; a deletion isn't, its page would show "not found" before it's left.
    createClient: refetchClientsAndWait,
    updateClient: changeClientsAndWait,
    // Its sources lose their client.
    deleteClient: changeClients,
    createProject: refetchClientsAndWait,
    // Its recaps and sources take its new client, and the Aktivitäten say so.
    updateProjectClient: (context: MutationContext) => {
        changeClients(context);
        void context.queryClient.invalidateQueries({
            queryKey: ['workspaces', context.workspaceId],
            predicate: (query) => query.queryKey.includes('activities'),
        });
    },
    updateProject: changeClientsAndWait,
    createPublicLink: refetchClients,
    deletePublicLink: refetchClients,
    renewClientEmailAddress: refetchClients,
    updateClientEmailAddress: refetchClients,
    renewProjectEmailAddress: refetchClients,
    updateProjectEmailAddress: refetchClients,
    deleteProject: refetchClients,
    // Held until the clients are fetched again, where the rail and the star read it (plan task 67).
    starClient: ({ queryClient, workspaceId }) =>
        queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'clients'] }),
    starProject: ({ queryClient, workspaceId }) =>
        queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'clients'] }),
    // The Übersicht's clients follow the teams you're on (plan/DECISIONS.md D143).
    updateClientTeam: changeTeams,
    updateProjectTeam: changeTeams,
    // Who reads the project, as on the employee (D144).
    updateProjectEmployees: changeReaders,
    uploadClientLogo: refetchClients,
    deleteClientLogo: refetchClients,
    uploadProjectLogo: refetchClients,
    deleteProjectLogo: refetchClients,
    // A client's employees and invitations are below it in the cache.
    createEmployeeInvitation: refetchClients,
    deleteEmployeeInvitation: refetchClients,
    removeEmployee: changeReaders,
    // How a disabled employee reads changes everywhere in the workspace (plan task 64).
    updateEmployee: ({ queryClient, workspaceId }) =>
        void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId] }),
    // Leaving their client takes the workspace out of the list.
    leaveClient: ({ queryClient, workspaceId }) => removeWorkspaceFromCache(queryClient, workspaceId!),
    // Answers the workspace, which the employee opens next.
    acceptEmployeeInvitation: writeWorkspaceTo,

    // Sources and recaps
    uploadSource: addSource,
    createSourceFromLink: (context, result: SourceFromLinkResult) => addSource(context, result.source),
    // Several picked at once (plan task 39), each written like a link's before the list is fetched once.
    createSourcesFromPick: (context, results: SourceFromLinkResult[]) => {
        results.forEach(({ source }) =>
            context.queryClient.setQueryData(['workspaces', context.workspaceId, 'sources', source.id], source)
        );
        return context.queryClient.invalidateQueries({
            queryKey: ['workspaces', context.workspaceId, 'sources', 'list'],
        });
    },
    createRecapFromSources: (context, recap: Recap) => {
        setRecap(context, recap);
        refetchClients(context);
    },
    // Not part of a version; whom its published version asks follows the new project.
    updateRecapProject: (context, recap: Recap) => {
        context.queryClient.setQueryData(['workspaces', context.workspaceId, 'recaps', recap.public_id], recap);
        changeReaders(context);
    },
    // Changes nothing it says, so only the recap is set (plan task 54).
    createRecapPublicLink: (context, recap: Recap) =>
        context.queryClient.setQueryData(['workspaces', context.workspaceId, 'recaps', recap.public_id], recap),
    deleteRecapPublicLink: (context, recap: Recap) =>
        context.queryClient.setQueryData(['workspaces', context.workspaceId, 'recaps', recap.public_id], recap),
    processRecap: setRecap,
    updateRecap: setEditedRecap,
    createPerson: setEditedRecap,
    updatePerson: setEditedRecap,
    deletePerson: setEditedRecap,
    createTopic: setEditedRecap,
    updateTopic: setEditedRecap,
    moveTopic: setEditedRecap,
    deleteTopic: setEditedRecap,
    createDecision: setEditedRecap,
    updateDecision: setEditedRecap,
    moveDecision: setEditedRecap,
    deleteDecision: setEditedRecap,
    createTodo: setEditedRecap,
    updateTodo: setEditedRecap,
    moveTodo: setEditedRecap,
    // Shown on every version too, as it is now.
    markTodoDone: setEditedRecap,
    deleteTodo: setEditedRecap,
    createEvent: setEditedRecap,
    updateEvent: setEditedRecap,
    moveEvent: setEditedRecap,
    deleteEvent: setEditedRecap,
    // Drafted anew from other files, so it's processing, and the sources' pages list the recaps they're in.
    updateSourceFiles: (context, recap: Recap) => {
        setEditedRecap(context, recap);
        refetchSources(context);
    },
    publishRecap: setEditedRecap,
    // Asks someone else on the published version, which makes no draft (plan/DECISIONS.md D60).
    updateAcknowledgers: (context, recap: Recap) => {
        context.queryClient.setQueryData(['workspaces', context.workspaceId, 'recaps', recap.public_id], recap);
        void context.queryClient.invalidateQueries({
            queryKey: ['workspaces', context.workspaceId, 'recaps', recap.public_id, 'versions'],
        });
        refetchRecapLists(context);
    },
    // Everything a client's employee sees says who acknowledged what: the recap, its versions, the lists and the home.
    acknowledgeRecap: ({ queryClient, workspaceId }) =>
        void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'client'] }),
    // Their home and the recap show it, and its versions too, as it is now.
    markEmployeeTodoDone: ({ queryClient, workspaceId }) =>
        void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'client'] }),
    discardDraft: setEditedRecap,
    restoreVersion: setEditedRecap,
    // Removed first, so a page still showing it doesn't fetch it again (404).
    deleteRecap: (context, _: void, { publicId }: DeleteRecapMutationVariables) => {
        context.queryClient.removeQueries({ queryKey: ['workspaces', context.workspaceId, 'recaps', publicId] });
        // The Übersicht's first recap (plan task 76).
        void context.queryClient.invalidateQueries({ queryKey: ['workspaces', context.workspaceId, 'home'] });
        refetchRecapLists(context);
        refetchSources(context);
        refetchClients(context);
    },
    processSource: setSource,
    // Taking it processes it again, like processSource; its recaps follow once it's read.
    takeSourceUpdate: setSource,
    // Its client's projects count the sources waiting in them.
    updateSource: (context, source: Source) => {
        setSource(context, source);
        refetchClients(context);
    },
    updateSources: (context) => {
        refetchSources(context);
        refetchClients(context);
    },
    // The start on the Übersicht (plan task 124).
    hideStart: refetchHome,
    ignoreStartSuggestion: refetchHome,
    finishStart: refetchHome,
    createStartClient: changeStart,
    passStartStep: changeStart,
    reopenStartStep: refetchHome,
    deleteSource: (context, _: void, { sourceId }: DeleteSourceMutationVariables) => {
        context.queryClient.removeQueries({ queryKey: ['workspaces', context.workspaceId, 'sources', sourceId] });
        refetchSources(context);
        refetchRecaps(context);
        refetchClients(context);
    },

    // Views (plan task 61); every change refetches the rail's marks besides (useMutationBehavior).
    markSectionSeen: refetchSeenList,
    // Its news are read anew for this view, without showing the last view's meanwhile; the rows lose their dots
    // (plan task 62).
    visitRecap: (context, _: void, { publicId }: VisitRecapMutationVariables) => {
        void context.queryClient.resetQueries({
            queryKey: ['workspaces', context.workspaceId, 'recaps', publicId, 'news'],
        });
        refetchNews(context);
        refetchRecapLists(context);
        refetchClients(context);
    },
    visitSource: refetchNews,
    visitEmployeeRecap: ({ queryClient, workspaceId }, _: void, { publicId }: VisitEmployeeRecapMutationVariables) => {
        void queryClient.resetQueries({ queryKey: ['workspaces', workspaceId, 'client', 'recaps', publicId, 'news'] });
        // Their Übersicht and Recaps.
        void queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId, 'client', 'list'] });
    },

    // Comments
    createRecapComment: refetchComments,
    createSourceComment: refetchComments,
    updateComment: refetchComments,
    deleteComment: refetchComments,
    createEmployeeRecapComment: refetchComments,
    updateEmployeeComment: refetchComments,
    deleteEmployeeComment: refetchComments,

    // Notifications, whose settings are the account's (plan/DECISIONS.md D95)
    updateNotificationSetting: ({ queryClient, workspaceId }, settings: NotificationSetting[]) =>
        queryClient.setQueryData(['workspaces', workspaceId, 'notification-settings', 'list'], settings),
    updateEmployeeNotificationSetting: ({ queryClient, workspaceId }, settings: NotificationSetting[]) =>
        queryClient.setQueryData(['workspaces', workspaceId, 'client', 'list', 'notification-settings'], settings),
    muteRecap: ({ queryClient, workspaceId }, mute: RecapMute, { publicId }: MuteRecapMutationVariables) =>
        queryClient.setQueryData<Recap>(
            ['workspaces', workspaceId, 'recaps', publicId],
            (recap) => recap && { ...recap, is_muted: mute.is_muted }
        ),
    muteEmployeeRecap: (
        { queryClient, workspaceId },
        mute: RecapMute,
        { publicId }: MuteEmployeeRecapMutationVariables
    ) =>
        queryClient.setQueryData<EmployeeRecap>(
            ['workspaces', workspaceId, 'client', 'recaps', publicId],
            (recap) => recap && { ...recap, is_muted: mute.is_muted }
        ),

    // Connections. Starting a sync answers with `is_syncing`, and the list fetched again starts its polling.
    createConnection: changeConnectionsAndMembers,
    updateConnection: changeConnectionsAndMembers,
    deleteConnection: (context) => {
        changeConnectionsAndMembers(context);
        refetchExternalCompanies(context);
    },
    renewConnectionToken: refetchConnections,
    createFeed: refetchConnections,
    updateFeed: refetchConnections,
    deleteFeed: (context) => {
        removeConnections(context);
        refetchExternalCompanies(context);
    },
    createRoute: refetchConnections,
    updateRoute: refetchConnections,
    deleteRoute: refetchConnections,
    syncFeed: refetchConnections,
    syncFeeds: refetchConnections,
    renewFeedSecret: refetchConnections,
    renewFeedEmailAddress: refetchConnections,
    updateFeedEmailAddress: refetchConnections,
    createClientFromExternalCompany: refetchExternalCompanies,
    updateExternalCompany: refetchExternalCompanies,
    // A client's employee's own connections (plan task 35), which bring no sources in.
    createEmployeeConnection: refetchConnections,
    updateEmployeeConnection: refetchConnections,
    deleteEmployeeConnection: refetchConnections,
    createEmployeeFeed: refetchConnections,
    updateEmployeeFeed: refetchConnections,
    deleteEmployeeFeed: refetchConnections,
    syncEmployeeFeed: refetchConnections,
    renewEmployeeFeedSecret: refetchConnections,
    renewEmployeeConnectionToken: refetchConnections,
};

// orval calls this with the mutation's options, its route and the operation.
export const useMutationBehavior = <Options extends { onSuccess?: (...args: never[]) => unknown }>(
    options: Options,
    route: { url: string },
    operation: { operationId: string; operationName: string }
): Options => {
    const workspaceId = useContext(WorkspaceIdContext);
    const queryClient = useQueryClient();
    const rule = MUTATION_RULES[operation.operationName];
    const ownOnSuccess = options.onSuccess as ((...args: unknown[]) => unknown) | undefined;

    return {
        ...options,
        // The hook's own onSuccess (a page's leavePage) comes first, so the page is gone before the cache changes and
        // never refetches what no longer exists.
        onSuccess: async (...args: unknown[]) => {
            await ownOnSuccess?.(...args);
            // Most changes in a workspace change what its rail counts or marks as new (plan task 61), visiting a
            // section too.
            if (workspaceId) {
                void queryClient.invalidateQueries({
                    queryKey: ['workspaces', workspaceId],
                    predicate: (query) => query.queryKey.includes('attention'),
                });
            }
            await rule?.({ queryClient, workspaceId }, args[0] as never, args[1] as never);
        },
    };
};
